import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import PublicPageShell from '../components/PublicPageShell';
import NotFound from './NotFound';
import { useLanguage } from '../contexts/LanguageContext';
import {
  getNextResource,
  getRelatedServices,
  getLocalizedResourceBySlug,
  resourcePath,
  servicePath,
} from '../data/contentLibrary';
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '../utils/analytics';

const ResourceDetailPage = () => {
  const { slug } = useParams();
  const { currentLang, t } = useLanguage();
  const resource = getLocalizedResourceBySlug(slug, currentLang);

  useEffect(() => {
    if (resource) {
      trackAnalyticsEvent(ANALYTICS_EVENTS.RESOURCE_VIEWED, {
        resourceSlug: resource.slug,
      });
    }
  }, [resource]);

  if (!resource) return <NotFound />;

  const relatedServices = getRelatedServices(resource.relatedServices, currentLang);
  const nextResource = getNextResource(resource.nextResource, currentLang);

  return (
    <PublicPageShell mainClassName="pt-20">
      <article className="relative overflow-hidden bg-black">
        <div className="hero-grid absolute inset-0 pointer-events-none" />
        <div className="relative mx-auto max-w-[980px] px-5 py-16 md:px-[7.6923%] md:py-24">
          <nav className="mb-8 flex flex-wrap gap-2 text-sm text-white/44" aria-label={t('common.breadcrumb')}>
            <Link to="/" className="hover:text-[#00FFD1]">{t('nav.home')}</Link>
            <span>/</span>
            <Link to="/resources" className="hover:text-[#00FFD1]">{t('nav.resources')}</Link>
            <span>/</span>
            <span className="text-white/70">{resource.title}</span>
          </nav>

          <header>
            <p className="mb-4 text-sm font-semibold uppercase text-[#00FFD1]">
              {t('common.guideEyebrow')}
            </p>
            <h1 className="text-4xl font-semibold leading-tight text-white md:text-6xl">
              {resource.title}
            </h1>
            <p className="mt-6 text-base leading-8 text-white/64 md:text-lg">
              {resource.heroAnswer}
            </p>
            <p className="mt-5 text-sm text-white/42">
              {t('common.author')}: {resource.author} | {t('common.lastUpdated')}: {resource.lastUpdated}
            </p>
          </header>

          <div className="mt-12 space-y-10">
            {resource.sections.map((section) => (
              <section key={section.heading} className="border-t border-white/[0.08] pt-8">
                <h2 className="text-3xl font-semibold text-white">{section.heading}</h2>
                <div className="mt-5 space-y-5 text-base leading-8 text-white/62">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <aside className="mt-12 grid gap-5 border-t border-white/[0.08] pt-8 md:grid-cols-2">
            <section>
              <h2 className="text-2xl font-semibold text-white">{t('common.relatedServices')}</h2>
              <div className="mt-5 grid gap-3">
                {relatedServices.map((service) => (
                  <Link
                    key={service.slug}
                    to={servicePath(service.slug)}
                    className="group flex items-center justify-between rounded-xl border border-white/[0.07] bg-white/[0.025] px-4 py-3 text-white/68 hover:border-[#00FFD1]/35 hover:text-white"
                  >
                    <span>{service.title}</span>
                    <ArrowRight className="h-4 w-4 text-[#00FFD1] transition-transform group-hover:translate-x-1" />
                  </Link>
                ))}
              </div>
            </section>

            {nextResource && (
              <section>
                <h2 className="text-2xl font-semibold text-white">{t('common.nextResource')}</h2>
                <Link
                  to={resourcePath(nextResource.slug)}
                  className="group mt-5 flex items-center justify-between rounded-xl border border-white/[0.07] bg-white/[0.025] px-4 py-3 text-white/68 hover:border-[#00FFD1]/35 hover:text-white"
                >
                  <span>{nextResource.title}</span>
                  <ArrowRight className="h-4 w-4 text-[#00FFD1] transition-transform group-hover:translate-x-1" />
                </Link>
              </section>
            )}
          </aside>

          <footer className="mt-12 rounded-2xl border border-[#00FFD1]/20 bg-[#00FFD1]/[0.04] p-6">
            <p className="text-sm leading-6 text-white/62">
              {resource.sources.length
                ? t('common.externalSources')
                : t('common.noExternalClaims')}
            </p>
            <Link to={resource.cta.path} className="mt-6 inline-flex btn-glass-primary">
              {resource.cta.label}
            </Link>
          </footer>
        </div>
      </article>
    </PublicPageShell>
  );
};

export default ResourceDetailPage;
